import {cloneDeep} from 'lodash';
import {Fragment, useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {agentActionsApi, clientConnectApi, groupApi, userApi} from 'api';
import {
    ClientConnectFormDialog, Icon, PredefinedNotifierType, ClientConnectCreatedModal, ClientConnectMessageDialog, IconProps, Tooltip
} from 'components';
import {useApiData, useUserSession} from 'hooks';
import {AccountAssignmentInfo, ClientParticipant, Group} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {ClientConnect, UserSummary} from 'types';
import {transformUtils} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.COMMON.CLIENT_CONNECT;

type Props = {
    client: ClientParticipant;
    isSessionActive?: boolean;
    agentEmail?: string;
    assignment?: AccountAssignmentInfo;
    clientConnectId?: string;
    groups?: Group[];
    users?: UserSummary[];
    className?: string;
    iconProps?: Partial<IconProps>;
}

export const CreateClientConnectionButton: React.FC<Props> = ({
    client, clientConnectId, isSessionActive, agentEmail, assignment,
    groups: groupsProp, users: usersProp, className, iconProps
}: Props) => {
    const {t} = useTranslation();
    const [localConnect, setLocalConnect] = useState<Partial<ClientConnect>>(null);
    const [formDialogOpen, setFormDialogOpen] = useState(false);
    const [createdModalOpen, setCreatedModalOpen] = useState(false);
    const [additionalDataRequested, setAdditionalDataRequested] = useState(false);
    const [createdConnect, setCreatedConnect] = useState(null);
    const [messageDialogOpen, setMessageDialogOpen] = useState(false);

    const openCreatedModal = useCallback(() => {
        setCreatedModalOpen(true);
    }, []);

    const closeFormDialog = useCallback(() => {
        setFormDialogOpen(false);
        setLocalConnect(null);
    }, []);

    const onDeleteSuccess = useCallback(() => {
        setCreatedModalOpen(false);
        setCreatedConnect(null);
    }, []);

    const onCreateSuccess = useCallback((connect: ClientConnect) => {
        setCreatedConnect(connect);
        closeFormDialog();
        openCreatedModal();
    }, [openCreatedModal, closeFormDialog]);

    const {sessionParams} = useUserSession();

    const createConnectFn = useCallback((data: Partial<ClientConnect>) => {
        if (isSessionActive) {
            return agentActionsApi.createClientConnect(sessionParams, data);
        } else {
            return clientConnectApi.create(data);
        }
    }, [sessionParams, isSessionActive]);

    const {send: createConnect, loading: createLoading, error: createError} = useApiData(createConnectFn,
        {onSuccess: onCreateSuccess, notificationType: PredefinedNotifierType.SaveError});

    const {send: loadConnect, loading: connectLoading} = useApiData(clientConnectApi.getOne, {
        onSuccess: onCreateSuccess
    });
    const {send: updateConnect} = useApiData(clientConnectApi.update, {
        onSuccess: onCreateSuccess
    });

    const deleteConnectFn = useCallback(() => {
        if (isSessionActive) {
            return agentActionsApi.deleteClientConnect(sessionParams);
        } else {
            return clientConnectApi.deleteOne(createdConnect?.id);
        }
    }, [sessionParams, createdConnect, isSessionActive]);

    const {send: deleteConnect} = useApiData(deleteConnectFn, {
        onSuccess: onDeleteSuccess
    });

    const {send: loadGroups, data: loadedGroups, loading: loadingGroups} = useApiData(groupApi.getGroups);
    const {send: loadUsers, data: loadedUsers = [], loading: loadingUsers} =
        useApiData<UserSummary[]>(userApi.getUsersSummary);

    const groups = useMemo(() => groupsProp || loadedGroups, [groupsProp, loadedGroups]);
    const users = useMemo(() => usersProp || loadedUsers, [usersProp, loadedUsers]);

    const additionalDataLoading = useMemo(() => loadingGroups || loadingUsers || connectLoading,
        [loadingGroups, loadingUsers, connectLoading]);

    const loading = useMemo(() =>
        createLoading || additionalDataLoading,
    [createLoading, additionalDataLoading]);

    const loadAdditionalData = useCallback(() => {
        if (!usersProp) {
            loadUsers();
        }
        if (!groupsProp) {
            loadGroups();
        }
    }, [usersProp, groupsProp, loadUsers, loadGroups]);

    const handleClick = useCallback(() => {
        if (clientConnectId) {
            loadConnect(clientConnectId);
            loadAdditionalData();
            setAdditionalDataRequested(true);
            return;
        }

        if (!additionalDataRequested) {
            loadAdditionalData();
            setAdditionalDataRequested(true);
        }
        setLocalConnect(cloneDeep(transformUtils.fromParticipantToClientConnect(client, {assignment, agentEmail})));
        setFormDialogOpen(true);
    }, [client, assignment, agentEmail, additionalDataRequested, clientConnectId, loadConnect, loadAdditionalData]);

    const handleUpdateClick = useCallback(() => {
        setLocalConnect(createdConnect);
        setCreatedModalOpen(false);
        setFormDialogOpen(true);
    }, [createdConnect]);

    const handleDeleteClick = useCallback(() => {
        if (createdConnect?.id || clientConnectId) {
            deleteConnect();
        }
    }, [createdConnect, clientConnectId, deleteConnect]);

    const onCloseFormDialog = useCallback((confirm: boolean, connect: Partial<ClientConnect>) => {
        if (confirm) {
            if (createdConnect) {
                updateConnect(createdConnect.id, connect);
            } else {
                createConnect(connect);
            }

        } else {
            closeFormDialog();
        }
    }, [createdConnect, createConnect, updateConnect, closeFormDialog]);

    const onCloseCreatedModal = useCallback(() => {
        setCreatedModalOpen(false);
    }, []);

    const onCloseMessageDialog = useCallback(() => {
        setMessageDialogOpen(false);
    }, []);

    const handleSendMessage = useCallback(() => {
        onCloseCreatedModal();
        setMessageDialogOpen(true);
    }, [onCloseCreatedModal]);

    return (
        <Fragment>
            <Tooltip message={clientConnectId ? t(T_KEYS.UPDATE) : t(T_KEYS.CREATE)} disabled={formDialogOpen}>
                <Icon name='create-link' button margin={false} className={className} {...iconProps} onClick={handleClick} />
            </Tooltip>
            {additionalDataRequested && !additionalDataLoading &&
                <Fragment>
                    <ClientConnectFormDialog
                        open={formDialogOpen}
                        mode={createdConnect ? 'update' : 'create'}
                        users={users}
                        connect={createdConnect || localConnect}
                        groups={groups}
                        loading={loading}
                        error={createError}
                        substituteContacts={!createdConnect}
                        onClose={onCloseFormDialog}
                    />
                    <ClientConnectCreatedModal
                        open={createdModalOpen}
                        connect={createdConnect}
                        onSendMessage={handleSendMessage}
                        onUpdate={handleUpdateClick}
                        onDelete={handleDeleteClick}
                        onClose={onCloseCreatedModal}
                    />
                    <ClientConnectMessageDialog
                        open={messageDialogOpen}
                        connect={createdConnect}
                        onClose={onCloseMessageDialog}
                    />
                </Fragment>}
        </Fragment>
    );
};
